import type {
  ControlKind,
  EvaluationScenario,
  ProtocolApplicability,
  RelationKind,
  ScenarioChangePart,
  ScenarioCheck,
  ScenarioDecisionKind,
  ScenarioReviewPolicy,
  ToolDoubleOutcome,
} from '@/api/scenarioVersions';

// ─────────────────────────────────────────────────────────────────────────────
// Scenario words — how a v2 scenario reads on screen (screens 05 and 10). One home for the sentence a check becomes, so
// the review's "What changed?" and the editor say the same thing about the same check. No JSON and no operator names
// reach the reader. The meaning of each operator is the runner's (AgentScenarioValidator); these sentences describe it.
// ─────────────────────────────────────────────────────────────────────────────

const q = (s: string) => `“${s}”`;
const list = (items: string[] | undefined, joiner: string) => (items ?? []).filter((s) => s?.trim()).map(q).join(joiner);

/** One check as a sentence, e.g. "Calls A2A_ConsultPeer with peerName = triage-nurse-oncology". */
export function checkSentence(c: ScenarioCheck): string {
  const a = c.args ?? {};
  const withArgs = Object.entries(a.withArgs ?? {}).map(([k, v]) => `${k} = ${v}`).join(', ');
  const tool = a.tool || (a.toolPattern ? `a tool matching ${q(a.toolPattern)}` : 'a tool');
  const answer = a.responseContains?.length ? ` and its answer mentions ${list(a.responseContains, ' and ')}` : '';
  switch (c.operator) {
    case 'tool.called':
      return `Calls ${tool}${withArgs ? ` with ${withArgs}` : ''}${answer}`;
    case 'tool.notCalled':
      return `Does not call ${(a.values ?? []).join(' or ') || 'the named tool'}`;
    case 'tool.notCalledWith':
      return `Does not call ${tool}${withArgs ? ` with ${withArgs}` : ''}${answer}`;
    case 'claim.stated':
      return `States the figure ${q(a.value ?? '')}${a.sitsWith?.length ? ` beside ${list(a.sitsWith, ', ')}` : ''}${a.neverWith?.length ? `, never beside ${list(a.neverWith, ', ')}` : ''}`;
    case 'skills.loaded':
      return `Loads the skill ${(a.values ?? []).join(' and ')}`;
    case 'skills.notLoaded':
      return `Does not load the skill ${(a.values ?? []).join(' or ')}`;
    case 'reply.containsAll':
      return `The reply mentions every one of ${list(a.values, ', ')}`;
    case 'reply.containsAny':
      return `The reply mentions at least one of ${list(a.values, ' OR ')}`;
    case 'reply.containsNone':
      return `The reply mentions none of ${list(a.values, ', ')}`;
    case 'reply.severityScreenComplete':
      return 'The reply asks about at least three of the five emergency topics (fever, chest pain, blood, fluids, fainting)';
    case 'conversation.neverSays':
      return `No reply in the conversation says ${list(a.values, ' or ')}`;
    case 'conversation.saysAtLeastOnce':
    case 'conversation.containsAnyOf':
      return `Somewhere in the conversation a reply says at least one of ${list(a.values, ' OR ')}`;
    case 'conversation.numbersGrounded':
      return a.enabled === false
        ? 'Numbers in replies are not checked against tool output'
        : `Every number in a reply appears in a tool’s output${a.tolerance != null ? ` (within ${a.tolerance})` : ''}${a.ignorePatterns?.length ? `, ignoring ${list(a.ignorePatterns, ', ')}` : ''}`;
    case 'peer.consulted':
      return `Asks the peer ${a.value || '(no peer named)'}`;
    case 'peer.notConsulted':
      return `Does not ask the peer ${a.value || '(no peer named)'}`;
    case 'manual.obligation':
      return `A reviewer judges: ${a.value ? q(a.value) : '(not written yet)'}`;
    default:
      return `A check this screen cannot describe (${c.operator})`;
  }
}

/** Where a check looks: "Turn 1" or "Whole conversation · rule “Never names a dose”". */
export function checkPlace(c: ScenarioCheck): string {
  if (c.scope === 'Turn') return `Turn ${(c.turnIndex ?? 0) + 1}`;
  return c.label ? `Whole conversation · rule ${q(c.label)}` : 'Whole conversation';
}

/** A check's short name for lists and refusals: its place and its position there ("Turn 1 · check 2"). */
export function checkName(s: EvaluationScenario, c: ScenarioCheck): string {
  const same = s.checks.filter((x) => x.scope === c.scope && (c.scope === 'Turn' ? x.turnIndex === c.turnIndex : x.ruleIndex === c.ruleIndex));
  const at = same.findIndex((x) => x.id === c.id) + 1;
  return `${checkPlace(c)} · check ${at > 0 ? at : '?'}`;
}

/** What evidence a check reads. Every v2 operator reads evidence an isolated run collects. */
export function checkEvidence(c: ScenarioCheck): string {
  if (c.operator === 'manual.obligation') return 'The reply, read by a reviewer';
  if (c.operator.startsWith('peer.')) return 'The peers the agent consulted on the turn';
  if (c.operator.startsWith('tool.')) return 'Tool calls, their arguments and answers';
  if (c.operator.startsWith('skills.')) return 'Skills loaded on the turn';
  if (c.operator === 'claim.stated') return 'The reply, clause by clause';
  if (c.operator === 'conversation.numbersGrounded') return 'Every reply and every tool’s output';
  if (c.operator.startsWith('conversation.')) return 'Every agent reply in the conversation';
  return 'The agent’s reply on the turn';
}

/** Keyword-strength checks can pass a wrong answer that uses the words, or fail a safe paraphrase. */
export function strengthWords(c: ScenarioCheck): { label: string; tone: 'green' | 'amber' | 'ink'; hint: string } {
  if (c.strength === 'Manual')
    return { label: 'Reviewer judges', tone: 'ink', hint: 'A person decides this obligation. No run marks it passed; the result waits for a reviewer.' };
  return c.strength === 'Exact'
    ? { label: 'Observable fact', tone: 'green', hint: 'Tests something the run records: a tool call, a skill, a figure in its clause.' }
    : { label: 'Keyword-strength', tone: 'amber', hint: 'Tests words in the reply. It can pass a wrong answer that uses them, or fail a safe paraphrase.' };
}

export const KIND_WORDS: Record<string, string> = {
  ToolEffect: 'Tool effect',
  Prohibition: 'Prohibition',
  Consistency: 'Consistency',
  Grounding: 'Grounding',
  Communication: 'Communication',
  InformationAcquisition: 'Information acquisition',
};

/** True for a check whose alternatives are OR (one of them is enough); every check is ANDed with the others. */
export function isAlternatives(c: ScenarioCheck): boolean {
  return ['reply.containsAny', 'conversation.saysAtLeastOnce', 'conversation.containsAnyOf'].includes(c.operator);
}

export const PART_WORDS: Record<ScenarioChangePart, string> = {
  Checks: 'checks',
  WhatTheUserSays: 'what the user says',
  Context: 'context',
  Basis: 'hazard and sources',
  ToolDoubles: 'tool answers',
  Controls: 'controls',
  Purpose: 'purpose',
  Relations: 'sibling links',
  Notes: 'author’s notes',
};

/** The parts every version has, in the server's order. Version 3's parts are named only when they changed. */
const EVERY_VERSION_PARTS: ScenarioChangePart[] = ['Checks', 'WhatTheUserSays', 'Context', 'Purpose', 'Notes'];

/** "Unchanged: what the user says, context, purpose." for the parts every version has that did not change. */
export function unchangedParts(changed: ScenarioChangePart[]): string {
  const rest = EVERY_VERSION_PARTS.filter((p) => !changed.includes(p)).map((p) => PART_WORDS[p]);
  return rest.length ? `Unchanged: ${rest.join(', ')}.` : '';
}

/** The decision's question, which already says how big the change is (mockup 05, marker 2). */
export function decisionQuestion(number: number, policy: ScenarioReviewPolicy, changed: ScenarioChangePart[]): string {
  if (policy === 'FirstReview') return `Approve version ${number}? It has not been approved in this form before.`;
  if (changed.includes('Checks')) return `Approve version ${number}? Its checks changed.`;
  if (changed.includes('WhatTheUserSays')) return `Approve version ${number}? What the user says changed.`;
  if (changed.includes('Context')) return `Approve version ${number}? Its context changed.`;
  if (changed.includes('Basis')) return `Approve version ${number}? Its hazard or sources changed.`;
  if (changed.includes('ToolDoubles')) return `Approve version ${number}? What its tools answer changed.`;
  if (changed.includes('Controls')) return `Approve version ${number}? Its controls changed.`;
  if (changed.includes('Relations')) return `Approve version ${number}? Only its wording or sibling links changed; the checks did not.`;
  return `Approve version ${number}? Only its wording changed; the checks did not.`;
}

export const POLICY_WORDS: Record<ScenarioReviewPolicy, { group: string; short: string }> = {
  FirstReview: { group: 'One at a time', short: 'not approved before' },
  Material: { group: 'One at a time', short: 'changes what is tested' },
  WordingOnly: { group: 'Wording only', short: 'wording · checks unchanged' },
};

export const DECISION_WORDS: Record<ScenarioDecisionKind, { label: string; tone: 'green' | 'amber' | 'red' | 'slate' }> = {
  Approved: { label: 'Recorded approval', tone: 'green' },
  ChangesRequested: { label: 'Changes requested', tone: 'amber' },
  Rejected: { label: 'Rejected', tone: 'red' },
  Withdrawn: { label: 'Approval withdrawn', tone: 'slate' },
};

export const ORIGIN_WORDS: Record<string, string> = {
  MigratedFromFile: 'Moved from the file library by the migration',
  AuthoredInStudio: 'Written in Agent Studio',
  Generated: 'Drafted by the Scenario model and added to the library',
};

/** The three behaviours of a check when the scenario binds no protocol (mockup 10, frame B). */
export const APPLICABILITY_WORDS: Record<ProtocolApplicability, { label: string; detail: string }> = {
  Applies: { label: 'Runs', detail: 'Needs no protocol. It uses no protocol facts and runs normally.' },
  NotApplicable: { label: 'Not applicable', detail: 'A reviewed rule says it does not apply without a protocol; recorded with that reason.' },
  NeedsProtocol: { label: 'Blocked', detail: 'Needs protocol facts that are missing. The run is refused, naming what must be supplied.' },
};

/** What a tool double makes the tool do, in words. */
export const DOUBLE_OUTCOME_WORDS: Record<ToolDoubleOutcome, string> = {
  Succeeds: 'Succeeds with this answer',
  Fails: 'Fails with this error',
  TimesOut: 'Times out',
  NotFiled: 'Finds nothing on file',
};

/** "double: Fails" — the label a run records on a call the scenario's double answered — in words. */
export function doubledCallWords(label: string | null | undefined): string {
  const outcome = /^double:\s*(\w+)/i.exec(label ?? '')?.[1] as ToolDoubleOutcome | undefined;
  const said = outcome && DOUBLE_OUTCOME_WORDS[outcome];
  return said ? `Answered by the scenario’s double: ${said.charAt(0).toLowerCase()}${said.slice(1)}` : 'Answered by the scenario’s double';
}

export const RELATION_WORDS: Record<RelationKind, { label: string; detail: string }> = {
  Paraphrase: { label: 'Paraphrase', detail: 'The same facts in other words: the expected behaviour must not change.' },
  ContextChange: { label: 'Context change', detail: 'One fact changed: the expected behaviour should change with it.' },
  Baseline: { label: 'Baseline', detail: 'The case this one varies.' },
};

export const CONTROL_WORDS: Record<ControlKind, { label: string; detail: string }> = {
  SafeParaphrase: { label: 'Safe reply', detail: 'A right answer in other words. The checks must pass it.' },
  UnsafeMutation: { label: 'Unsafe reply', detail: 'A wrong answer that may use the right words. The checks must fail it.' },
};

/** The roles a reviewer can declare. Studio records the role; it does not check it (D-SI-6). */
export const REVIEWER_ROLES = ['Agent designer', 'Clinical reviewer', 'Oncology nurse', 'Pharmacist', 'Physician', 'Operations reviewer'];

/** First and last characters of a hash, as the mockups show them (a91c…0e37). */
export function shortHash(hash: string | null | undefined): string {
  if (!hash) return '—';
  return hash.length <= 10 ? hash : `${hash.slice(0, 4)}…${hash.slice(-4)}`;
}
